<template>
  <div class="form__fieldset">
    <h3 class="form__title">{{ in18['page_step_3'] }}</h3>

    <!-- Address match question -->
    <div class="mb-4 form__group--md form__group--flex">
      <p class="mb-2 fw-500">{{ in18['payment_address_question'] }}</p>
    </div>
    <div class="mb-4 form__group--md form__group--flex">
      <div class="radio">
        <input
            id="payment_address_same_no"
            v-model="isPaymentSameAsShipping"
            :value="false"
            type="radio"
            class="radio__input"
        />
        <label class="radio__label" for="payment_address_same_no">
          {{ in18['payment_address_same_no'] }}
        </label>
      </div>
      <div class="radio">
        <input
            id="payment_address_same_yes"
            v-model="isPaymentSameAsShipping"
            :value="true"
            type="radio"
            class="radio__input"
        />
        <label class="radio__label" for="payment_address_same_yes">
          {{ in18['payment_address_same_yes'] }}
        </label>
      </div>
    </div>

    <!-- Country selector with flag -->
    <div v-if="!isPaymentSameAsShipping" class="mb-6 form__group form__group--md select-with-flag">
      <Multiselect
          :key="multiselectKey"
          class="relative border-b border-gray-300 outline-none country-multiselect"
          :disabled="inProgress"
          :options="countries"
          label="title"
          :searchable="true"
          :internal-search="true"
          :allow-empty="false"
          :show-labels="false"
          :placeholder="in18['attribute_shipping_country_option']"
          track-by="id"
          :custom-label="country => country.title"
          v-model="countryForMultiselect"
          :preserve-search="true"
          @focus="handleMultiselectFocus"
          @blur="handleMultiselectBlur"
          @select="handleSelect"
          ref="countryMultiselect"
          @search-change="onSearchChange"
      >
          <template #singleLabel="{ option }">
              <div class="flex justify-between items-center px-2 py-[18px] w-full pointer-events-none" v-if="option">
                  <div class="flex gap-2 items-center pl-2">
                      <img
                          v-if="option.code"
                          :src="`/shop/images/flags/${option.code.toLowerCase()}.svg`"
                          class="mr-2 w-7 h-5"
                          :alt="option.code"
                      />
                      <span class="mr-2 text-[16px] font-medium leading-none">{{ option.title }}</span>
                  </div>
              </div>
              <div class="flex justify-between items-center px-2 py-[18px] w-full pointer-events-none" v-else>
                  <span>{{ in18['attribute_shipping_country_option'] }}</span>
              </div>
          </template>
          <template #option="{ option }">
              <div class="flex gap-2 items-center">
                  <img 
                      :src="`/shop/images/flags/${option.code.toLowerCase()}.svg`"
                      class="mr-2 w-7 h-5"
                      :alt="option.code" 
                  />
                  <span>{{ option.title }}</span>
              </div>
          </template>
          <template #noResult>
              <div class="px-3 py-2 text-gray-600">
                  {{ in18['search_no_results_found'] || 'No results found' }}
              </div>
          </template>
      </Multiselect>
    </div>

    <!-- Payment methods -->
    <div v-if="!initialLoading" class="row">
      <!-- Non-credit payment methods -->
      <div
          v-if="paymentMethods.some(m => !m.is_credit)"
          class="col-md-6"
      >
        <div class="mb-4">
          <p class="mb-2 fw-500">{{ in18['payment_methods'] }}</p>
        </div>
        <div
            v-for="paymentMethod in paymentMethods.filter(m => !m.is_credit)"
            :key="paymentMethod.code"
            class="mb-4"
        >
          <div :class="{ 'disabled': inProgress }" class="radio-btn d-flex align-items-start">
            <input
                :id="paymentMethod.code"
                v-model="selectedPaymentMethod"
                :value="paymentMethod"
                class="radio-btn__input-native"
                name="payment-method"
                type="radio"
                @change="selectPaymentMethod"
            />
            <label :for="paymentMethod.code" class="radio-btn__label">
              <picture>
                <source :srcset="paymentMethod.image.webp" type="image/webp"/>
                <img :alt="paymentMethod.title" :src="paymentMethod.image.source" class="radio-btn__icon"/>
              </picture>
              <div class="radio-btn__title">{{ paymentMethod.title }}</div>
              <div class="radio-btn__text">{{ paymentMethod.description }}</div>
            </label>
          </div>
        </div>
      </div>

      <!-- Credit payment methods -->
      <div
          v-if="paymentMethods.some(m => m.is_credit)"
          class="col-md-6"
      >
        <div class="mb-4">
          <p class="mb-2 fw-500">{{ in18['credit_methods'] }}</p>
        </div>
        <div
            v-for="paymentMethod in paymentMethods.filter(m => m.is_credit)"
            :key="paymentMethod.code"
            class="mb-4"
        >
          <div :class="{ 'disabled': inProgress }" class="radio-btn d-flex align-items-start">
            <input
                :id="paymentMethod.code"
                v-model="selectedPaymentMethod"
                :value="paymentMethod"
                class="radio-btn__input-native"
                name="payment-method"
                type="radio"
                @change="selectPaymentMethod"
            />
            <label :for="paymentMethod.code" class="radio-btn__label">
              <picture>
                <source :srcset="paymentMethod.image.webp" type="image/webp"/>
                <img :alt="paymentMethod.title" :src="paymentMethod.image.source" class="radio-btn__icon"/>
              </picture>
              <div class="radio-btn__title">{{ paymentMethod.title }}</div>
              <div class="radio-btn__text">{{ paymentMethod.description }}</div>
            </label>
          </div>
        </div>
      </div>

      <!-- Validation error -->
      <div class="col-12">
        <validation-error :errors="errors['payment_method']"/>
      </div>
    </div>

    <div v-if="initialLoading" class="my-5 loader"></div>
  </div>
</template>

<script>
import ValidationError from '../../features/ValidationError.vue';
import Multiselect from 'vue-multiselect';

export default {
  name: 'CheckoutPayment',
  components: {
    'validation-error': ValidationError,
    Multiselect
  },

  props: {
    in18:                   {
      type: Object
    },
    errors:                 {
      type: Object
    },
    cart:                   {
      type: Object
    },
    inProgress:             {
      type: Boolean
    },
    selectedShippingMethod: {
      type:    Object,
      default: null
    },
  },

  emits: ['update-cart',
    'update-payment-method',
    'update-in-progress'],

  data() {
    return {
      paymentMethods:           [],
      selectedPaymentMethod:    null,
      initialLoading:           false,
      countries:                [],
      selectedPaymentCountryId: null,
      isPaymentSameAsShipping:  true,
      keepDropdownOpen:       false,
      countryForMultiselect:  null,
      multiselectKey: 0,

    };
  },

  computed: {
    selectedPaymentCountry() {
      return this.countries.find(c => c.id === this.selectedPaymentCountryId);
    }
  },

  created() {
    this.fetchCountries();
  },

  mounted() {
    // Initialize payment country from shipping when component is mounted
    if (this.isPaymentSameAsShipping && this.$parent.shipping?.country_id) {
      this.selectedPaymentCountryId = this.$parent.shipping.country_id;
    }
    
    // Also initialize the multiselect model if needed
    if (this.selectedPaymentCountryId && this.countries.length > 0) {
      const country = this.countries.find(c => c.id === this.selectedPaymentCountryId);
      if (country) {
        this.countryForMultiselect = country;
      }
    }
  },

  watch: {
    isPaymentSameAsShipping(val) {
      if (val) {
        this.selectedPaymentCountryId = this.$parent.shipping?.country_id || 112;
      }
      this.getPaymentMethods();
    },
    selectedPaymentCountryId(newVal) {
      if (!this.isPaymentSameAsShipping) {
        this.getPaymentMethods();
      }
    },
    countryForMultiselect(newCountry) {
      if (newCountry && (!this.selectedPaymentCountryId || newCountry.id !== this.selectedPaymentCountryId)) {
        this.selectedPaymentCountryId = newCountry.id;
      }
    },
    countries: {
      handler(newCountries) {
        // When countries are loaded, ensure the multiselect model is initialized
        if (newCountries.length > 0 && this.selectedPaymentCountryId) {
          const country = newCountries.find(c => c.id === this.selectedPaymentCountryId);
          if (country) {
            this.countryForMultiselect = country;
          }
        }
      },
      immediate: true
    },
    selectedShippingMethod: {
      handler() {
        if (this.isPaymentSameAsShipping) {
          this.selectedPaymentCountryId = this.$parent.shipping?.country_id || 112;
        }
        this.getPaymentMethods();
      },
      deep: true
    }
  },

  methods: {
    fetchCountries() {
      axios
          .get('/api/shipping/countries')
          .then(response => {
            this.countries = response.data.countries;
            this.selectedPaymentCountryId = this.$parent.shipping?.country_id || 112;
            this.getPaymentMethods();
          })
          .catch(error => console.error('Failed to load countries:', error));
    },

    getPaymentMethods() {
      if (!this.selectedShippingMethod?.id || !this.selectedPaymentCountryId) return;

      this.initialLoading = true;
      this.paymentMethods = [];
      this.selectedPaymentMethod = null;

      const delay = new Promise(resolve => setTimeout(resolve, 1000));
      const request = axios
          .get(`/api/payment?shipping_method_id=${this.selectedShippingMethod.id}&payment_country_id=${this.selectedPaymentCountryId}`)
          .then(response => {
            this.paymentMethods = response.data.paymentMethods;
          })
          .catch(error => console.error('Error loading payment methods:', error));

      Promise.all([request,
        delay]).finally(() => {
        this.selectedPaymentMethod = this.cart.payment_method ?? null;
        this.initialLoading = false;
        this.$emit('update-in-progress', false);
      });
    },

    selectPaymentMethod() {
      this.$emit('update-in-progress', true);
      return axios
          .post('/api/cart/payment/' + this.selectedPaymentMethod.id)
          .then(response => {
            this.$emit('update-payment-method', this.selectedPaymentMethod);
            this.$emit('update-cart', response.data.cart);
            ga_add_payment_info_event(response.data.cart);
          })
          .catch(error => {
            window.toastr.error(error.response?.data?.message || 'Failed to select payment method');
          });
    },
    getFlagUrl(countryCode) {
            if (!countryCode) return "";
            return `/shop/images/flags/${countryCode.toLowerCase()}.svg`;
        },
    handleMultiselectFocus() {
      // This ensures the dropdown doesn't immediately close after opening
      this.keepDropdownOpen = true;
      
      // Force the dropdown to open
      if (this.$refs.countryMultiselect) {
        this.$nextTick(() => {
          // Make sure the multiselect is activated properly
          if (!this.$refs.countryMultiselect.isOpen) {
            this.$refs.countryMultiselect.activate();
          }
        });
      }
    },
    
    handleMultiselectBlur(event) {
      // We need to prevent the dropdown from closing when clicking on options
      // We'll use a small delay to let click events inside the dropdown register first
      setTimeout(() => {
        // Check if the new focus target is inside our component
        if (!this.$el.contains(document.activeElement)) {
          this.keepDropdownOpen = false;
        }
      }, 100);
    },
    
    onSearchChange(query) {
      // When user types in search, make sure dropdown stays open
      this.keepDropdownOpen = true;
      
      // Force the dropdown to stay open
      if (this.$refs.countryMultiselect && !this.$refs.countryMultiselect.isOpen) {
        this.$nextTick(() => {
          this.$refs.countryMultiselect.activate();
        });
      }
    },
    handleSelect() {
      // Use setTimeout to ensure this runs after all Vue's internal event handling
      setTimeout(() => {
        if (this.$refs.countryMultiselect) {
          this.multiselectKey++;
          // Force close the dropdown
          this.$refs.countryMultiselect.isOpen = false;
          
          // Reset any internal state that might reopen it
          this.keepDropdownOpen = false;
          
          // Remove focus from the component to prevent auto-reopen
          if (document.activeElement) {
            document.activeElement.blur();
          }
        }
      }, 50);
    },
  }
};
</script>

<style>
/* Country multiselect styling */
.country-multiselect {
  transition: border-color 0.2s ease;
  cursor: pointer;
}

.multiselect__single{
  pointer-events: none;
  width: 100%;
}
.multiselect__placeholder{
  pointer-events: none;
}
.multiselect__input{
  padding: 15px 2px 15px 4px!important;
}

.country-multiselect.multiselect--active {
  border-bottom: 1px solid #273276;
}

.country-multiselect.multiselect--disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.country-multiselect .multiselect__placeholder{
  padding: 15px 2px 15px 4px!important;
}

/* Custom chevron that appears only when not showing placeholder */
.country-multiselect:not(.multiselect--active):not(.multiselect--disabled)::after {
  content: '';
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%) rotate(45deg);
  width: 8px;
  height: 8px;
  border-right: 2px solid #6B7280;
  border-bottom: 2px solid #6B7280;
  pointer-events: none;
  transition: transform 0.2s ease;
}

.country-multiselect.multiselect--active::after {
  content: '';
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%) rotate(-135deg);
  width: 8px;
  height: 8px;
  border-right: 2px solid #273276;
  border-bottom: 2px solid #273276;
  pointer-events: none;
  transition: transform 0.2s ease;
}

/* Input and container styles */
.country-multiselect .multiselect__tags {
  border: none;
  padding: 0;
  background: transparent;
  border-radius: 0;
  min-height: 0;
  display: flex;
  align-items: center;
}

.country-multiselect .multiselect__input {
  background: transparent;
  border: none;
  outline: none;
  font-size: 16px;
  padding: 18px 2px 18px 4px;
  margin: 0;
  width: 100%;
  max-width: 100%;
  box-shadow: none !important;
  font-family: inherit;
  color: inherit;
}

.country-multiselect .multiselect__placeholder {
  padding: 18px 2px 18px 4px;
  margin: 0;
  color: inherit;
  opacity: 0.7;
}

.country-multiselect .multiselect__single {
  background: transparent;
  padding: 0;
  margin: 0;
}

/* Dropdown styling */
.country-multiselect .multiselect__content-wrapper {
  padding: 4px;
  position: absolute;
  left: 0;
  top: 100%;
  width: 100%;
  z-index: 999;
  background-color: white;
  border: 1px solid #e8e8e8;
  border-radius: 0 0 5px 5px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  overflow: auto;
}

.country-multiselect .multiselect__content {
  width: 100%;
}

.country-multiselect .multiselect__element {
  cursor: pointer;
  width: 100%;
  display: block;
}

/* Option styling */
.country-multiselect .multiselect__option {
  padding: 8px;
  min-height: 40px;
  line-height: 20px;
  text-decoration: none;
  text-transform: none;
  display: block;
  width: 100%;
  box-sizing: border-box;
  border-radius: 4px;
  margin: 2px 0;
}

/* Option states */
.country-multiselect .multiselect__option--highlight {
  background: #f3f5f9;
  color: #000;
}

.country-multiselect .multiselect__option--selected {
  background: #edf2f7;
  color: #000;
  font-weight: bold;
}

.country-multiselect .multiselect__option--selected.multiselect__option--highlight {
  background: #e2e8f0;
  color: #000;
}

/* Prevent dropdown toggle issues */
.country-multiselect .multiselect__select {
  pointer-events: none;
}
</style>